import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface Permission {
  id: string;
  name: string;
  resource: string;
  actions: string[];
  scope: string;
  description: string;
}

interface Role {
  id: string;
  name: string;
  display_name: string;
  permission_ids: string[];
  level: number;
}

interface UserPermissions {
  role: Role | null;
  permissions: Permission[];
  isLoading: boolean;
  canAccess: (resource: string, action?: string) => boolean;
  hasAnyPermission: (resources: string[]) => boolean;
}

/**
 * Hook to fetch and manage user permissions from RBAC system
 * This replaces hardcoded role permissions with database-driven access control
 */
export function usePermissions(): UserPermissions {
  const { user } = useAuth();
  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        // Get user's assigned role
        const roleResponse = await axios.get(
          `${API_URL}/api/v1/settings/users/${user.id}/roles`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const userRole = roleResponse.data.role;
        if (!userRole) {
          setIsLoading(false);
          return;
        }

        setRole(userRole);

        // Get all permissions
        const permissionsResponse = await axios.get(
          `${API_URL}/api/v1/settings/permissions`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Filter to only permissions assigned to user's role
        const rolePermissions = permissionsResponse.data.filter((perm: Permission) =>
          userRole.permission_ids.includes(perm.id)
        );

        setPermissions(rolePermissions);
      } catch (error) {
        console.error('Failed to load user permissions:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserPermissions();
  }, [user?.id, API_URL]);

  /**
   * Check if user has access to a specific resource and action
   */
  const canAccess = (resource: string, action?: string): boolean => {
    if (!permissions.length) return false;

    const resourcePerms = permissions.filter(p => p.resource === resource);
    if (!resourcePerms.length) return false;

    // If no specific action required, just check if resource exists
    if (!action) return true;

    // Check if any permission includes the required action
    return resourcePerms.some(p => p.actions.includes(action));
  };

  /**
   * Check if user has permission for any of the given resources
   */
  const hasAnyPermission = (resources: string[]): boolean => {
    if (!permissions.length) return false;
    return resources.some(resource => canAccess(resource));
  };

  return {
    role,
    permissions,
    isLoading,
    canAccess,
    hasAnyPermission,
  };
}
